package com.hyperether.goodjob.scenes.jobs

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.MaterialTheme
import androidx.compose.material.OutlinedButton
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.CompletionReportStatus
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.adding_proof
import com.hyperether.goodjob.resources.additional_notes_report
import com.hyperether.goodjob.resources.approve
import com.hyperether.goodjob.resources.client_feedback
import com.hyperether.goodjob.resources.client_signature
import com.hyperether.goodjob.resources.documents
import com.hyperether.goodjob.resources.employee_comments
import com.hyperether.goodjob.resources.final_inspection
import com.hyperether.goodjob.resources.health_confirmation
import com.hyperether.goodjob.resources.no
import com.hyperether.goodjob.resources.no_damage
import com.hyperether.goodjob.resources.no_information
import com.hyperether.goodjob.resources.reject
import com.hyperether.goodjob.resources.status_check
import com.hyperether.goodjob.resources.task_completion_report
import com.hyperether.goodjob.resources.was_damage_caused
import com.hyperether.goodjob.resources.were_safety_met
import com.hyperether.goodjob.resources.yes
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.scenes.components.pointerCursorIfEnabled
import com.hyperether.goodjob.scenes.register.FilledButton
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.GrayBackgroundWarning
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CompletionReportOverviewView(
    viewModel: JobDetailsViewModel,
    completionReportResponse: CompletionReportResponse?,
    isEmployee: Boolean,
    jobFinished: Boolean,
    onApproveClick: () -> Unit,
    onRejectClick: () -> Unit,
    onOpenPdfClick: (String) -> Unit
) {
    val completionReportImages = viewModel.completionReportImagePainters.collectAsState()
    val clientSignatureImages = viewModel.clientSignaturePainters.collectAsState()
    val completionReportPdfs = viewModel.completionReportPdfs.collectAsState()

    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.toScaleDp())
    ) {
        if (completionReportResponse != null) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomCenter)
            ) {
                // Header - WEB
                if (!getPlatformChecker().isMobile()) {
                    Text(
                        fontSize = 27.toScaleSp(),
                        text = stringResource(Res.string.task_completion_report),
                        fontWeight = FontWeight.W700
                    )

                    Spacer(modifier = Modifier.height(10.toScaleDp()))
                }
                // Title
                Text(
                    fontSize = 20.toScaleSp(),
                    text = stringResource(Res.string.adding_proof),
                    fontWeight = FontWeight.W700
                )
                Spacer(modifier = Modifier.height(8.toScaleDp()))
                // WEB LAYOUT
                if (!getPlatformChecker().isMobile()) {
                    if (completionReportImages.value.isNotEmpty() || completionReportPdfs.value.isNotEmpty()) {
                        CompletionReportImageList(
                            items = completionReportImages.value,
                            onOpenImageClick = { painter ->
                                viewModel.onOpenImageClick(painter)
                            })

                        if (completionReportPdfs.value.isNotEmpty()) {
                            Spacer(modifier = Modifier.height(12.toScaleDp()))

                            Column(verticalArrangement = Arrangement.spacedBy(8.toScaleDp())) {
                                Text(
                                    text = stringResource(Res.string.documents),
                                    style = MaterialTheme.typography.h6,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.toScaleSp()
                                )
                                for ((fileName, filePath) in completionReportPdfs.value) {
                                    DocumentRowWeb(
                                        documentName = fileName,
                                        author = "",
                                        date = "",
                                        openPdf = {
                                            onOpenPdfClick(filePath)
                                        },
                                        onDeleteFileClick = {},
                                        isOverview = true
                                    )
                                }
                            }
                        }
                    } else {
                        Text(
                            text = stringResource(Res.string.no_information),
                            fontSize = 14.toScaleSp()
                        )
                    }
                }
                // MOBILE LAYOUT
                else {
                    // Images Row
                    if (completionReportImages.value.isNotEmpty() || completionReportPdfs.value.isNotEmpty()) {
                        CompletionReportImageList(
                            items = completionReportImages.value,
                            onOpenImageClick = { painter ->
                                viewModel.onOpenImageClick(painter)
                            })

                        if (completionReportPdfs.value.isNotEmpty()) {
                            Spacer(modifier = Modifier.height(12.toScaleDp()))

                            Column(verticalArrangement = Arrangement.spacedBy(8.toScaleDp())) {
                                Text(
                                    text = stringResource(Res.string.documents),
                                    style = MaterialTheme.typography.h6,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.toScaleSp()
                                )
                                for ((fileName, filePath) in completionReportPdfs.value) {
                                    DocumentRow(
                                        isEmployee = isEmployee,
                                        documentName = fileName,
                                        author = "",
                                        date = "",
                                        openPdf = {
                                            onOpenPdfClick(filePath)
                                        },
                                        onDeleteFileClick = {},
                                        isOverview = true
                                    )
                                }
                            }
                        }
                    } else {
                        Text(
                            text = stringResource(Res.string.no_information),
                            fontSize = 14.toScaleSp()
                        )
                    }
                }

                Spacer(modifier = Modifier.height(12.toScaleDp()))

                // Status Check Section
                SectionTitle(stringResource(Res.string.status_check))
                if (completionReportResponse.damage == true) {
                    Column {
                        Text(
                            text = stringResource(Res.string.was_damage_caused),
                            fontSize = 14.toScaleSp(),
                            modifier = Modifier.padding(vertical = 8.toScaleDp())
                        )

                        IssueReportView(stringResource(Res.string.yes))

                        Text(
                            text = completionReportResponse.damageDescription ?: "",
                            fontSize = 14.toScaleSp(),
                            fontWeight = FontWeight.W700,
                            modifier = Modifier
                                .background(GrayBackgroundWarning)
                                .padding(all = 6.toScaleDp())
                        )
                    }
                } else {
                    CheckBoxItem(
                        question = stringResource(Res.string.was_damage_caused),
                        answer = stringResource(Res.string.no_damage)
                    )
                }

                Spacer(modifier = Modifier.height(12.toScaleDp()))
                if (completionReportResponse.correctFunctionality == false) {
                    Column {
                        Text(
                            text = stringResource(Res.string.final_inspection),
                            fontSize = 14.toScaleSp(),
                            modifier = Modifier.padding(vertical = 8.toScaleDp())
                        )
                        IssueReportView(stringResource(Res.string.no))
                        Text(
                            text = completionReportResponse.reasonForIncompleteInspection
                                ?: stringResource(Res.string.no_information),
                            fontSize = 14.toScaleSp(),
                            fontWeight = FontWeight.W700,
                            modifier = Modifier
                                .background(GrayBackgroundWarning)
                                .padding(all = 6.toScaleDp())
                        )
                    }
                } else {
                    CheckBoxItem(
                        question = stringResource(Res.string.final_inspection),
                        answer = stringResource(Res.string.yes)
                    )
                }
                Spacer(modifier = Modifier.height(10.toScaleDp()))
                // Client Feedback Section
                SectionTitle(stringResource(Res.string.client_feedback))
                Text(
                    fontSize = 14.toScaleSp(),
                    text = "${stringResource(Res.string.client_feedback)}:",
                    style = MaterialTheme.typography.subtitle1
                )

                Text(
                    text = if (completionReportResponse.clientFeedback?.isNotEmpty() == true) "\"${completionReportResponse.clientFeedback}\""
                    else stringResource(Res.string.no_information),
                    style = MaterialTheme.typography.body1,
                    fontWeight = if (completionReportResponse.clientFeedback?.isNotEmpty() == true) FontWeight.W700 else null,
                    lineHeight = 18.toScaleSp(),
                    fontSize = 14.toScaleSp(),
                    modifier = Modifier.padding(vertical = 8.toScaleDp())
                )

                Spacer(modifier = Modifier.height(16.toScaleDp()))
                Text(stringResource(Res.string.client_signature), fontSize = 14.toScaleSp())
                // Placeholder for Signature
                if (clientSignatureImages.value.isNotEmpty()) {
                    Row(
                        horizontalArrangement = Arrangement.Start,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        if (clientSignatureImages.value.isNotEmpty()) {
                            val imagePainter = clientSignatureImages.value.first().second
                            Image(
                                painter = imagePainter,
                                contentDescription = null,
                                contentScale = ContentScale.Crop,
                                modifier = Modifier
                                    .width(120.toScaleDp())
                                    .height(100.toScaleDp())
                                    .clip(RoundedCornerShape(8.toScaleDp()))
                                    .clickableWithPointer {
                                        viewModel.onOpenImageClick(imagePainter)
                                    }
                            )
                        }
                    }
                } else {
                    Text(
                        text = stringResource(Res.string.no_information),
                        fontSize = 14.toScaleSp()
                    )
                }

                Spacer(modifier = Modifier.height(18.toScaleDp()))
                // Additional Notes Section
                SectionTitle(stringResource(Res.string.additional_notes_report))
                Text(
                    text = stringResource(Res.string.employee_comments),
                    fontSize = 14.toScaleSp(),
                    style = MaterialTheme.typography.subtitle1
                )
                Text(
                    text = if (completionReportResponse.additionalNotes?.isNotEmpty() == true) "\"${completionReportResponse.additionalNotes}\""
                    else stringResource(Res.string.no_information),
                    fontSize = 14.toScaleSp(),
                    fontStyle = if (completionReportResponse.additionalNotes?.isNotEmpty() == true) FontStyle.Italic else null,
                    modifier = Modifier.padding(vertical = 8.toScaleDp())
                )

                Spacer(modifier = Modifier.height(18.toScaleDp()))
                // Health and Safety Confirmation Section
                SectionTitle(stringResource(Res.string.health_confirmation))
                if (completionReportResponse.healthAndSafety == false) {
                    Column {
                        Text(
                            text = stringResource(Res.string.were_safety_met),
                            fontSize = 14.toScaleSp(),
                            modifier = Modifier.padding(vertical = 8.toScaleDp())
                        )
                        IssueReportView(stringResource(Res.string.no))
                        Text(
                            text = completionReportResponse.unmetSafetyRequirements
                                ?: stringResource(Res.string.no_information),
                            fontSize = 14.toScaleSp(),
                            fontWeight = FontWeight.W700,
                            modifier = Modifier
                                .background(GrayBackgroundWarning)
                                .padding(all = 6.toScaleDp())
                        )
                    }
                } else {
                    CheckBoxItem(
                        question = stringResource(Res.string.were_safety_met),
                        answer = stringResource(Res.string.yes)
                    )
                }
                // buttons
                Spacer(modifier = Modifier.height(18.toScaleDp()))
                if (!isEmployee && !jobFinished &&
                    completionReportResponse.status != CompletionReportStatus.rejected.name
                ) {
                    CompletionActionButtons(
                        onApproveClick = onApproveClick,
                        onRejectClick = onRejectClick
                    )
                }
            }
        }
    }
}

@Composable
private fun CompletionActionButtons(
    onApproveClick: () -> Unit,
    onRejectClick: () -> Unit
) {
    if (!getPlatformChecker().isMobile()) {
        // WEB LAYOUT
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 615.toScaleDp()),
            horizontalArrangement = Arrangement.spacedBy(35.toScaleDp())
        ) {
            FilledButton(
                onClick = onApproveClick,
                modifier = Modifier.weight(1f),
                text = stringResource(Res.string.approve)
            )

            OutlinedButton(
                onClick = onRejectClick,
                modifier = Modifier.weight(1f)
                    .height(50.toScaleDp())
                    .pointerCursorIfEnabled(true),
                border = BorderStroke(1.toScaleDp(), BlueApp),
                shape = RoundedCornerShape(50),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = Color.White,
                    backgroundColor = Color.Transparent
                )
            ) {
                DisableSelection {
                    Text(
                        text = stringResource(Res.string.reject),
                        maxLines = 1,
                        fontSize = 15.toScaleSp(),
                        color = BlueApp
                    )
                }
            }
        }
    }
    // MOBILE LAYOUT
    else {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.toScaleDp())
        ) {
            FilledButton(
                onClick = onApproveClick,
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(Res.string.approve)
            )

            OutlinedButton(
                onClick = onRejectClick,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(50.toScaleDp()),
                border = BorderStroke(1.toScaleDp(), BlueApp),
                shape = RoundedCornerShape(50),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = Color.White,
                    backgroundColor = Color.Transparent
                )
            ) {
                DisableSelection {
                    Text(
                        text = stringResource(Res.string.reject),
                        maxLines = 1,
                        fontSize = 15.toScaleSp(),
                        color = BlueApp
                    )
                }
            }
        }
    }
}